import { useEffect, useRef, useState } from 'react';
import type { BlobDownload } from '@/lib/api/client';
import type { DocumentPreview } from '../workflowApi';
import { saveDocument } from '../documentDownload';

export function DoctoralDocumentPreview({
  title,
  load,
  download,
  onClose,
}: {
  title: string;
  load: () => Promise<DocumentPreview>;
  download: () => Promise<BlobDownload>;
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<DocumentPreview>();
  const [url, setUrl] = useState('');
  const [mime, setMime] = useState('');
  const [error, setError] = useState('');
  const callbacks = useRef({ load, download });
  callbacks.current = { load, download };
  useEffect(() => {
    setPreview(undefined);
    setUrl('');
    setError('');
    let cancelled = false;
    let objectUrl = '';
    void callbacks.current
      .load()
      .then(async (data) => {
        if (cancelled) return;
        if (data.kind === 'unsupported') {
          const result = await callbacks.current.download();
          if (cancelled) return;
          const type = result.blob.type;
          if (
            type === 'application/pdf' ||
            ['image/png', 'image/jpeg', 'image/webp'].includes(type)
          ) {
            objectUrl = URL.createObjectURL(result.blob);
            setUrl(objectUrl);
            setMime(type);
          }
        }
        if (!cancelled) setPreview(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Не удалось открыть документ');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [title]);
  return (
    <section className="dp120-card span12" role="dialog" aria-modal="false" aria-label={title}>
      <header>
        <h3>{title}</h3>
        <button className="dp120-mini" onClick={onClose}>
          Закрыть просмотр
        </button>
      </header>
      <div className="dp120-card-body">
        {error && <p role="alert">{error}</p>}
        {!preview && !error && <p role="status">Открытие документа…</p>}
        {preview?.kind === 'docx' && (
          <>
            <p>Текстовый просмотр DOCX. Оригинальное оформление доступно в скачанном файле.</p>
            {preview.blocks?.map((block, i) =>
              block.kind === 'table' ? (
                <table key={i}>
                  <tbody>
                    {block.rows?.map((row, j) => (
                      <tr key={j}>
                        {row.map((cell, k) => (
                          <td key={k} style={{ whiteSpace: 'pre-wrap' }}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p key={i} style={{ whiteSpace: 'pre-wrap' }}>
                  {block.text}
                </p>
              ),
            )}
          </>
        )}
        {url &&
          (mime === 'application/pdf' ? (
            <iframe title={title} src={url} sandbox="" style={{ width: '100%', height: '70vh' }} />
          ) : (
            <img src={url} alt={title} style={{ maxWidth: '100%' }} />
          ))}
        {preview?.kind === 'unsupported' && !url && (
          <p>Просмотр этого формата доступен в приложении после скачивания.</p>
        )}
        <button
          className="dp120-mini gold"
          onClick={() =>
            void download()
              .then((result) => saveDocument(result, title))
              .catch((e: unknown) =>
                setError(e instanceof Error ? e.message : 'Не удалось скачать документ'),
              )
          }
        >
          Скачать оригинал
        </button>
      </div>
    </section>
  );
}
